/**
 * Which stretches of a countertop's edges are EXPOSED — the edge a fabricator
 * polishes and profiles, and the edge the 3D view cuts the profile into.
 *
 * Read off the piece's real plan outline (`planOutline`: a slab's rectangle,
 * or a corner piece's or an angled end's `polygonCorners`), one edge at a
 * time, with every stretch something stands against taken out:
 *
 *   - a WALL (the back of a wall run, an end dying into a wall);
 *   - another COUNTER (a seam; at a square corner the through slab's front
 *     where the other leg's slab starts; a leg's end against a corner piece);
 *   - anything else at COUNTER HEIGHT just outside the edge — a tall unit, a
 *     fridge, a range — the probe `countertopEnds.exposedEnds` asks of a slab's
 *     two ends, asked here of every stretch of every edge.
 *
 * What stands in the slab (a sink, a cooktop: objects parented to it), under
 * it (its cabinets, a dishwasher, a back panel) or on it (anything resting on
 * the top surface) closes nothing: none reaches the slab's own height band
 * outside its edge. Nor does a LIGHT, whatever its box says: a catalog
 * pendant's box is its whole drop, ceiling to shade, and one hung low over an
 * island reaches past the counter top — each left a square stretch in the
 * island's profiled edge under it (the user, 2026-11-03).
 *
 * Until this, exposure was guessed per piece — the front always, the back
 * only on an island, both ends of an island and neither of a wall run — so a
 * seam drew a profile into a joint, an open wall-run end stayed square, and
 * an outlined piece was neither profiled nor billed (the user, 2026-11-03:
 * "we're not keeping track of which sides of the countertop are actually
 * exposed edges").
 *
 * Pure. No I/O.
 */

const { planOutline, footprintFor } = require('../geometry/objectFootprint');
const { clipToHalfPlane } = require('../geometry/halfPlaneClip');
const {
  isCeilingLight,
  isWallLight,
} = require('../estimate-pricing/utils/lightingClassification');

/** How far inside the edge a neighbour may reach and still only abut it. */
const INSET_M = 0.01;
/** How far outside the edge a neighbour closes it: the end probe's reach. */
const REACH_M = 0.06;
/** A stretch shorter than this is rounding, not an edge. */
const MIN_SPAN_M = 0.005;
/**
 * A wall stored with no thickness (a face plane) is given this one — a hair,
 * so it reaches no further into the room than its face does.
 */
const MIN_WALL_THICKNESS_M = 0.001;

const sub = (a, b) => ({ x: a.x - b.x, z: a.z - b.z });
const dot = (a, b) => a.x * b.x + a.z * b.z;

function signedArea(poly) {
  let s = 0;
  for (let i = 0; i < poly.length; i += 1) {
    const p = poly[i];
    const q = poly[(i + 1) % poly.length];
    s += p.x * q.z - q.x * p.z;
  }
  return s / 2;
}

/** The vertical range an object occupies, or null when it does not say. */
function yRange(o) {
  const cy = o?.center?.y;
  const h = o?.dimensions?.y;
  if (!Number.isFinite(cy) || !(h > 0)) return null;
  return [cy - h / 2, cy + h / 2];
}

/**
 * Is this piece a corner piece — the angled corner's kite, or a counter piece
 * a bend adds (`bentRunFill.patchCountertop`)? It has no long axis, so none of
 * its edges is an END.
 */
const isCornerPiece = (top) =>
  top?.type === 'corner-fill-countertop' ||
  top?.category?.['corner-piece'] === true;

/**
 * An edge is an END when it runs across the slab's length (its local X): the
 * two short faces of a run or island slab, which `endFinish` governs. A
 * corner piece has none, and an angled end's 45° cut is no end either.
 */
function isEndEdge(top, d) {
  if (isCornerPiece(top)) return false;
  const ry = top?.rotation?.y || 0;
  const localX = { x: Math.cos(ry), z: -Math.sin(ry) };
  return Math.abs(dot(d, localX)) < 0.5;
}

/** Plan footprint of a wall: its box, given a thickness when it has none. */
function wallFootprint(w) {
  if (!w?.center || !(w?.dimensions?.x > 0)) return null;
  return footprintFor(
    w.center,
    w.dimensions.x,
    Math.max(w.dimensions.z || 0, MIN_WALL_THICKNESS_M),
    w.rotation?.y || 0
  );
}

/**
 * The things that may stand against an edge of `top`, each with its plan
 * footprint: every wall, and every object at the slab's own height that is
 * not the slab itself and not set in it.
 */
function blockersFor(top, { objects = [], walls = [] }) {
  const own = yRange(top);
  // the slab's middle half: what is beside the slab, not under or on it
  const band = own
    ? [own[0] + (own[1] - own[0]) / 4, own[1] - (own[1] - own[0]) / 4]
    : null;
  const atSlabHeight = (o) => {
    if (!band) return true;
    const r = yRange(o);
    return !r || (r[1] > band[0] + 1e-6 && r[0] < band[1] - 1e-6);
  };
  const setIn = (o) => (o?.parents || []).some((p) => p?.id && p.id === top.id);
  const isLight = (o) => isCeilingLight(o) || isWallLight(o);
  const out = [];
  for (const w of walls || []) {
    if (!atSlabHeight(w)) continue;
    const fp = wallFootprint(w);
    if (fp) out.push(fp);
  }
  for (const o of objects || []) {
    if (!o || o === top || (top.id && o.id === top.id)) continue;
    if (!o.center || !o.dimensions || setIn(o) || isLight(o)) continue;
    if (!atSlabHeight(o)) continue;
    const fp = planOutline(o);
    if (fp?.length >= 3) out.push(fp);
  }
  return out;
}

/**
 * The stretch of edge [0, len] (along `d` from `a`, outward `n`) a footprint
 * covers: what is left of it inside the band from INSET_M inside the edge to
 * REACH_M outside, projected onto the edge. Null when it does not reach.
 */
function coveredInterval(fp, a, d, n, len) {
  const local = fp.map((p) => {
    const r = sub(p, a);
    return { x: dot(r, d), z: dot(r, n) };
  });
  let clipped = clipToHalfPlane(local, (p) => -INSET_M - p.z);
  clipped = clipToHalfPlane(clipped, (p) => p.z - REACH_M);
  clipped = clipToHalfPlane(clipped, (p) => -p.x);
  clipped = clipToHalfPlane(clipped, (p) => p.x - len);
  if (clipped.length < 3) return null;
  const us = clipped.map((p) => p.x);
  const lo = Math.max(0, Math.min(...us));
  const hi = Math.min(len, Math.max(...us));
  return hi - lo > 1e-6 ? [lo, hi] : null;
}

/** [0, len] less the union of `covered`, stretches under MIN_SPAN_M dropped. */
function uncovered(len, covered) {
  const sorted = covered.slice().sort((p, q) => p[0] - q[0]);
  const out = [];
  let at = 0;
  for (const [lo, hi] of sorted) {
    if (lo > at) out.push([at, lo]);
    at = Math.max(at, hi);
  }
  if (at < len) out.push([at, len]);
  return out.filter(([lo, hi]) => hi - lo >= MIN_SPAN_M);
}

/**
 * Every edge of a countertop piece and the stretches of it that are exposed.
 *
 * @param {Object} top a countertop, a corner piece, an outlined slab
 * @param {{ objects?: Object[], walls?: Object[] }} [ctx] the room's objects
 *   (the top itself may be among them) and walls (center / dimensions /
 *   rotation, as `exposedEnds` takes them)
 * @returns {Array<{ a:{x,z}, b:{x,z}, length:number, normal:{x,z},
 *   end:boolean, exposed: Array<[number, number]> }>} in outline order;
 *   `exposed` are stretches along the edge from `a`, in metres
 */
function countertopEdges(top, ctx = {}) {
  const outline = planOutline(top);
  if (!outline || outline.length < 3) return [];
  const ccw = signedArea(outline) > 0;
  const blockers = blockersFor(top, ctx);
  const edges = [];
  for (let i = 0; i < outline.length; i += 1) {
    const a = outline[i];
    const b = outline[(i + 1) % outline.length];
    const length = Math.hypot(b.x - a.x, b.z - a.z);
    if (length < 1e-9) continue;
    const d = { x: (b.x - a.x) / length, z: (b.z - a.z) / length };
    // outward: the right-hand perpendicular of a counter-clockwise outline
    const normal = ccw ? { x: d.z, z: -d.x } : { x: -d.z, z: d.x };
    const covered = blockers
      .map((fp) => coveredInterval(fp, a, d, normal, length))
      .filter(Boolean);
    edges.push({
      a: { x: a.x, z: a.z },
      b: { x: b.x, z: b.z },
      length,
      normal,
      end: isEndEdge(top, d),
      exposed: uncovered(length, covered),
    });
  }
  return edges;
}

module.exports = {
  countertopEdges,
  isCornerPiece,
  INSET_M,
  REACH_M,
};
